/* Eloss — share
 *
 * Spec: §F.2 item 13 ("low weight, preserves current copy-link behaviour"),
 * §N (live regions), §P.5.
 *
 * The Liquid ships a real mailto: link so the control is never dead. This
 * upgrades it in place: navigator.share where the platform offers it,
 * clipboard copy everywhere else. The <a> is replaced by a <button> because
 * once it stops navigating it stops being a link.
 */

(() => {
  'use strict';

  const Eloss = window.Eloss;
  if (!Eloss) return;

  const RESET_DELAY = 2400;

  Eloss.define(
    'eloss-share',
    class ElossShare extends HTMLElement {
      connectedCallback() {
        this.status = this.querySelector('[data-share-status]');
        this.onClick = () => this.share();

        /* This element REWRITES its own markup on first connect: the <a>
           fallback is replaced by a <button>. A Theme Editor re-render (or any
           re-insertion) therefore reconnects an element whose fallback is
           already gone, so connect must handle both shapes.

           Getting this wrong is silent: the button stays on the page looking
           perfectly normal while disconnectedCallback has already taken its
           listener away, and sharing simply stops working. That is the same
           failure the F-2 fix addressed in the footer. */
        const existing = this.querySelector('.share__button');
        if (existing) {
          this.button = existing;
          this.button.addEventListener('click', this.onClick);
          return;
        }

        const fallback = this.querySelector('[data-share-fallback]');
        if (!fallback) return;

        const canCopy = Boolean(navigator.clipboard && window.isSecureContext);
        const canShare = typeof navigator.share === 'function';
        if (!canCopy && !canShare) return;

        this.button = document.createElement('button');
        this.button.type = 'button';
        this.button.className = 'share__button';
        this.button.innerHTML = fallback.innerHTML;
        this.button.addEventListener('click', this.onClick);

        fallback.replaceWith(this.button);
      }

      disconnectedCallback() {
        if (this.button && this.onClick) {
          this.button.removeEventListener('click', this.onClick);
        }
        clearTimeout(this.resetTimer);
      }

      announce(message) {
        if (!this.status) return;
        this.status.textContent = message;
        clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(() => {
          this.status.textContent = '';
        }, RESET_DELAY);
      }

      async share() {
        const url = this.dataset.shareUrl;
        const title = this.dataset.shareTitle;
        if (!url) return;

        if (typeof navigator.share === 'function') {
          try {
            await navigator.share({ title, url });
            return;
          } catch (error) {
            /* A dismissed share sheet is a normal outcome, not a failure —
               fall through to copy rather than reporting anything. */
            if (error.name === 'AbortError') return;
          }
        }

        try {
          await navigator.clipboard.writeText(url);
          this.announce(this.dataset.copiedMessage || '');
        } catch (error) {
          console.warn('Eloss: share failed', error);
        }
      }
    }
  );
})();
